/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Medical Parameters Edit/View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  getLastItemUrl,
  bottomOptionsCreateEdit,
  bottomOptionsRead,
  formItemParameters
} from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

let medicalParameterData = {};

/**
 * Medical Parameters View/Edit
 * @param { Object } medicalParameterActions - Medical Parameter Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const MedicalParametersViewEdit = ({ medicalParameterActions, actions }) => {
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();

  const isView = location.pathname.includes('view');

  const [isReadOnly] = React.useState(isView);
  const [medicalParameterInfo, setMedicalParameterInfo] = React.useState(null);
  const [medicalParameterId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );

  const { getMedicalParameterById, updateMedicalParameter } =
    medicalParameterActions;
  const { setLoading } = actions;

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set medical parameter info
  const onGetMedicalParameterInfo = (_, response) => {
    if (response.data) {
      const { param_key, param_value, param_description } = response.data;
      medicalParameterData = { param_key, param_value, param_description };
      setMedicalParameterInfo(
        { param_key, param_value, param_description },
        setLoading(false)
      );
    } else {
      onGoBack();
    }
  };

  React.useEffect(() => {
    getMedicalParameterById(
      { id: medicalParameterId },
      onGetMedicalParameterInfo
    );
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    updateMedicalParameter({ ...values, id: medicalParameterId }, onGoBack);
  };

  return (
    <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
      {medicalParameterInfo && (
        <FormGenerator
          initialValues={medicalParameterData}
          onFinish={onFinish}
          items={formItemParameters(translate, isReadOnly)}
          bottomOptions={
            !isReadOnly
              ? bottomOptionsCreateEdit(
                  onGoBack,
                  translate,
                  'edit',
                  styles.actionButton
                )
              : bottomOptionsRead(onGoBack, translate, styles.actionButton)
          }
        />
      )}
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
MedicalParametersViewEdit.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  medicalParameterActions: PropTypes.shape({
    getMedicalParameterById: PropTypes.func.isRequired,
    updateMedicalParameter: PropTypes.func.isRequired
  }).isRequired
};

export default MedicalParametersViewEdit;
